import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

const PLOT_W = 620;
const PLOT_H = 360;
const PAD_L  = 62;
const PAD_R  = 26;
const PAD_T  = 34;
const PAD_B  = 44;

const innerW = PLOT_W - PAD_L - PAD_R;
const innerH = PLOT_H - PAD_T - PAD_B;

const toX = (ipra) => PAD_L + ((Math.min(100, Math.max(0, ipra)) / 100) * innerW);
const toY = (iash) => PAD_T + innerH - ((Math.min(100, Math.max(0, iash)) / 100) * innerH);

const threshX = toX(50);
const threshY = toY(50);

const getBubbleColor = (ipra, iash) => {
    if (ipra < 65 && iash < 60)  return '#E74C3C';
    if (ipra < 70 || iash < 65)  return '#D4AC0D';
    return '#16A34A';
};

const toR = (n, maxN) => 10 + Math.round((Math.sqrt(Math.max(n, 1)) / Math.sqrt(Math.max(maxN, 1))) * 30);

const toRowMap = (arr = []) => {
    const out = {};
    arr.forEach((r) => { out[r.name] = Number(r.average); });
    return out;
};

const ManCrossPageLeaderScatter = ({ reportData, headerText, pageNumber }) => {
    const payload      = reportData?.payload || {};
    const ipraByArea   = useMemo(() => toRowMap(payload?.totals?.ipra?.average_by_management_area || []), [payload]);
    const iashByArea   = useMemo(() => toRowMap(payload?.totals?.iash?.average_by_management_area || []), [payload]);
    const headcountMap = useMemo(() => {
        const map = {};
        (payload?.headcount_by_management_area || []).forEach(e => { map[e.name] = e.headcount; });
        return map;
    }, [payload]);

    const points = useMemo(() => {
        const names = new Set([...Object.keys(ipraByArea), ...Object.keys(iashByArea)]);
        return [...names].map(name => ({
            name,
            ipra: ipraByArea[name] ?? null,
            iash: iashByArea[name] ?? null,
            n:    headcountMap[name] ?? 0,
        })).filter(p => p.ipra != null && p.iash != null);
    }, [ipraByArea, iashByArea, headcountMap]);

    const maxN = useMemo(() => Math.max(...points.map(p => p.n), 1), [points]);

    const ranked = useMemo(() => {
        return [...points]
            .map((p) => {
                const deficitIpra = Math.max(0, 100 - Number(p.ipra));
                const deficitIash = Math.max(0, 100 - Number(p.iash));
                const riskBoost = (p.ipra < 65 && p.iash < 60) ? 12 : 0;
                return { ...p, riskScore: deficitIpra + deficitIash + riskBoost };
            })
            .sort((a, b) => b.riskScore - a.riskScore || (b.n - a.n));
    }, [points]);

    // Coloca labels en columnas fijas (izq/der) y distribuye Y por cuadrante para evitar solapes.
    const resolvedPoints = useMemo(() => {
        const LABEL_GAP_Y = 17; // separación vertical mínima entre etiquetas
        const LABEL_H = 14;
        const LEFT_COL_X = PAD_L - 24;
        const RIGHT_COL_X = PLOT_W + 18;
        const TOP_MIN_Y = PAD_T + 10;
        const TOP_MAX_Y = threshY - 10;
        const BOT_MIN_Y = threshY + 10;
        const BOT_MAX_Y = PLOT_H - PAD_B - 10;

        const items = [...points]
            .sort((a, b) => b.n - a.n)
            .map((p) => {
                const cx = toX(p.ipra);
                const cy = toY(p.iash);
                const r  = toR(p.n, maxN);
                const fill = getBubbleColor(p.ipra, p.iash);
                // Área a la derecha del corte vertical (zona segura) etiqueta en columna derecha.
                const labelOnRight = cx >= threshX;
                const lx = labelOnRight ? RIGHT_COL_X : LEFT_COL_X;
                const labelW = Math.max(44, Math.round(p.name.length * 5.8));
                return { p, cx, cy, r, fill, labelOnRight, lx, ly: cy, labelW };
            });

        const distributeY = (group, minY, maxY) => {
            if (!group.length) return;

            const maxGapFromSpace = group.length > 1
                ? (maxY - minY) / (group.length - 1)
                : LABEL_GAP_Y;
            const effectiveGap = Math.max(12, Math.min(LABEL_GAP_Y, maxGapFromSpace));

            group.sort((a, b) => a.cy - b.cy);
            group.forEach((d, idx) => {
                d.ly = Math.max(minY, Math.min(maxY, d.cy + ((idx % 2 === 0) ? -4 : 4)));
            });

            for (let i = 1; i < group.length; i++) {
                const minAllowed = group[i - 1].ly + effectiveGap;
                if (group[i].ly < minAllowed) group[i].ly = minAllowed;
            }

            if (group[group.length - 1].ly > maxY) {
                const shift = group[group.length - 1].ly - maxY;
                group.forEach((d) => { d.ly -= shift; });
            }
            if (group[0].ly < minY) {
                const shift = minY - group[0].ly;
                group.forEach((d) => { d.ly += shift; });
            }

            for (let i = group.length - 2; i >= 0; i--) {
                const maxAllowed = group[i + 1].ly - effectiveGap;
                if (group[i].ly > maxAllowed) group[i].ly = maxAllowed;
            }

            group.forEach((d) => {
                d.ly = Math.max(minY, Math.min(maxY, d.ly));
                d.rectY = d.ly - LABEL_H / 2;
                d.rectX = d.labelOnRight ? d.lx - 4 : d.lx - d.labelW - 4;
                d.anchorX = d.labelOnRight ? d.lx - 6 : d.lx + 6;
            });
        };

        distributeY(items.filter(d => !d.labelOnRight && d.cy <= threshY), TOP_MIN_Y, TOP_MAX_Y);
        distributeY(items.filter(d => !d.labelOnRight && d.cy >  threshY), BOT_MIN_Y, BOT_MAX_Y);
        distributeY(items.filter(d => d.labelOnRight && d.cy <= threshY), TOP_MIN_Y, TOP_MAX_Y);
        distributeY(items.filter(d => d.labelOnRight && d.cy >  threshY), BOT_MIN_Y, BOT_MAX_Y);
        return items;
    }, [points, maxN]);

    const critical = ranked[0] || null;
    const q1Areas = ranked.filter((p) => p.ipra < 65 && p.iash < 60);
    const execLine = q1Areas.length > 1
        ? `${q1Areas[0].name} y ${q1Areas[1].name} están en Q1 crítico. Tamaño de burbuja = personas del área. Intervenir al supervisor tiene efecto multiplicador sobre todos sus operarios.`
        : critical
            ? `${critical.name} concentra la co-exposición más alta entre liderazgo y autocuidado. Tamaño de burbuja = personas del área. Intervenir al supervisor tiene efecto multiplicador sobre todos sus operarios.`
            : 'No hay áreas suficientes para análisis correlacional.';

    return (
        <div className="mgcr-leader-scatter-page">
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Correlación de Riesgo</h2>
                <p  className="mgcr-dx-subtitle">Liderazgo vs. Autocuidado</p>
            </div>

            <div className="mgcr-leader-scatter-board">
                <div className="mgcr-leader-scatter-left">
                    <div className="mgcr-leader-scatter-callout-row">
                        <span className="mgcr-leader-scatter-callout-title">Gráfico de Cuadrantes</span>
                        <span className="mgcr-leader-scatter-callout-divider" />
                        <span className="mgcr-leader-scatter-callout-text">
                            A menor IPRA del supervisor, mayor riesgo de sus operarios - correlación directa y predecible.
                        </span>
                    </div>

                    <div className="mgcr-leader-scatter-wrap">
                        <div className="mgcr-leader-scatter-axis-row mgcr-leader-scatter-axis-row--top">
                            <span className="mgcr-leader-scatter-axis-chip">Liderazgo débil / + Riesgo alto</span>
                            <span className="mgcr-leader-scatter-axis-chip">Liderazgo fuerte / + Autocuidado alto</span>
                        </div>
                    <svg
                        viewBox={`0 0 ${PLOT_W} ${PLOT_H}`}
                        className="mgcr-leader-scatter-svg"
                        style={{ width: '500px', height: 'auto', display: 'block' }}
                    >
                        <rect x={PAD_L} y={PAD_T} width={threshX - PAD_L} height={threshY - PAD_T} fill="#F6DDE1" />
                        <rect x={threshX} y={PAD_T} width={PLOT_W - PAD_R - threshX} height={threshY - PAD_T} fill="#DCECE4" />
                        <rect x={PAD_L} y={threshY} width={threshX - PAD_L} height={PLOT_H - PAD_B - threshY} fill="#EAE6D2" />
                        <rect x={threshX} y={threshY} width={PLOT_W - PAD_R - threshX} height={PLOT_H - PAD_B - threshY} fill="#EDE3DC" />

                        <line x1={threshX} y1={PAD_T} x2={threshX} y2={PLOT_H - PAD_B} stroke="#C5CDD6" strokeWidth="1.2" />
                        <line x1={PAD_L} y1={threshY} x2={PLOT_W - PAD_R} y2={threshY} stroke="#C5CDD6" strokeWidth="1.2" />

                        <text x={PAD_L + (threshX - PAD_L) / 2} y={PAD_T - 8} fontSize="10" fill="#7A91A7" textAnchor="middle">ZONA CRITICA</text>
                        <text x={threshX + (PLOT_W - PAD_R - threshX) / 2} y={PAD_T - 8} fontSize="10" fill="#7A91A7" textAnchor="middle">ZONA SEGURA</text>

                        <text x={PAD_L + (threshX - PAD_L) / 2} y={PLOT_H - PAD_B + 16} fontSize="10" fill="#7A91A7" textAnchor="middle">RIESGO CONDUCTUAL</text>
                        <text x={threshX + (PLOT_W - PAD_R - threshX) / 2} y={PLOT_H - PAD_B + 16} fontSize="10" fill="#7A91A7" textAnchor="middle">RIESGO DE LIDERAZGO</text>

                        {resolvedPoints.map(({ p, cx, cy, r, fill, labelOnRight, lx, ly, labelW, rectX, rectY, anchorX }) => (
                            <g key={p.name}>
                                <circle cx={cx} cy={cy} r={r} fill={fill} stroke="#fff" strokeWidth="2" />
                                <polyline
                                    points={`${cx},${cy} ${labelOnRight ? Math.min(cx + r + 10, anchorX - 14) : Math.max(cx - r - 10, anchorX + 14)},${ly} ${anchorX},${ly}`}
                                    fill="none"
                                    stroke="#FFFFFF"
                                    strokeWidth="3.2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <polyline
                                    points={`${cx},${cy} ${labelOnRight ? Math.min(cx + r + 10, anchorX - 14) : Math.max(cx - r - 10, anchorX + 14)},${ly} ${anchorX},${ly}`}
                                    fill="none"
                                    stroke={fill}
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <rect
                                    x={rectX}
                                    y={rectY}
                                    width={labelW + 8}
                                    height="14"
                                    rx="4"
                                    fill="rgba(255,255,255,0.9)"
                                    stroke="rgba(90,110,130,0.22)"
                                    strokeWidth="0.6"
                                />
                                <text
                                    x={lx}
                                    y={ly + 3}
                                    textAnchor={labelOnRight ? 'start' : 'end'}
                                    fontSize="9.5"
                                    fontWeight="600"
                                    fill={fill}
                                    paintOrder="stroke"
                                    stroke="rgba(255,255,255,0.95)"
                                    strokeWidth="1.2"
                                >
                                    {p.name}
                                </text>
                            </g>
                        ))}
                    </svg>
                        <div className="mgcr-leader-scatter-axis-row mgcr-leader-scatter-axis-row--bottom">
                            <span className="mgcr-leader-scatter-axis-chip">Liderazgo fuerte / + Riesgo operario</span>
                            <span className="mgcr-leader-scatter-axis-chip">Liderazgo débil / + Buen autocuidado</span>
                        </div>
                </div>
                </div>
            </div>

            <div className="mgcr-leader-scatter-panel">
                <div className="mgcr-leader-scatter-panel-main">
                    <p className="mgcr-leader-scatter-panel-title">Conclusión Ejecutiva</p>
                    <p className="mgcr-leader-scatter-panel-text">{execLine}</p>
                </div>

                <div className="mgcr-leader-scatter-highlight">
                    Intervenir al supervisor es 3x más eficiente que programas grupales de operarios.
                </div>
            </div>

            <div className="mgcr-insight-footer">
                <strong>Esto implica que:</strong>{' '}
                {critical
                    ? `Intervenir a los supervisores de ${critical.name} beneficia a sus ${critical.n || 0} operarios simultáneamente; es 3x más eficiente que programas grupales.`
                    : 'No hay información suficiente para priorizar área crítica.'
                }
            </div>
            
            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageLeaderScatter;
